/**
 * @fileoverview Sentry error tracking and logging utilities.
 */

import {useEffect} from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import type {Event, EventHint} from "@sentry/types";
import {reactAppEnvironment} from "../config";

function combinedEventSearchText(event: Event): string {
  const parts: string[] = [];
  if (event.message) {
    parts.push(event.message);
  }
  if (event.logentry?.message) {
    parts.push(event.logentry.message);
  }
  for (const ex of event.exception?.values ?? []) {
    if (ex.value) {
      parts.push(ex.value);
    }
    if (ex.type) {
      parts.push(ex.type);
    }
  }
  return parts.join(" ").toLowerCase();
}

const IGNORE_ERRORS_INIT: (string | RegExp)[] = [
  // Browser noise — not actionable
  /resizeobserver loop limit exceeded/i,
  /resizeobserver loop completed with undelivered notifications/i,
  /script error\.?/i,
  "Non-Error promise rejection captured with value: undefined",
  // Network noise — transient, not actionable
  /network request failed/i,
  /failed to fetch/i,
  /load failed/i,
  /network\s*error/i,
  /aborterror/i,
  "Request aborted",
  // Chunk loading — deploy-related, not actionable
  /chunkloaderror/i,
  /loading chunk/i,
  /loading css chunk/i,
];

function beforeSend(event: Event, _hint: EventHint): Event | null {
  if (event.type) {
    return event;
  }

  const status = event.contexts?.response?.status_code;
  if (status === 401 || status === 403) {
    return null;
  }

  const text = combinedEventSearchText(event);
  if (/http (401|403)\s*:/i.test(text)) {
    return null;
  }
  if (/- (401|403)(?:\s|$)/i.test(text) && /request failed:/i.test(text)) {
    return null;
  }

  return event;
}

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    replaysSessionSampleRate: 0.001,
    replaysOnErrorSampleRate: 1,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.01,
    environment: reactAppEnvironment,
    ignoreErrors: IGNORE_ERRORS_INIT,
    beforeSend,
  });
}

export const logErrorToSentry = import.meta.env.VITE_SENTRY_DSN
  ? (error: unknown, info: unknown = {}) => {
      if (error instanceof Response) {
        if (error.status === 401 || error.status === 403) {
          return;
        }
      } else if (
        typeof error === "object" &&
        error !== null &&
        "config" in error &&
        "data" in error
      ) {
        const httpError = error as {
          status?: number;
          response?: {status?: number};
        };
        const status = httpError.status ?? httpError.response?.status;
        if (status === 401 || status === 403) {
          return;
        }
      }

      Sentry.withScope(scope => {
        Object.keys(info).forEach(key => {
          // @ts-expect-error info error
          scope.setExtra(key, info[key]);
        });

        // Better error serialization
        let errorString;
        if (error instanceof Response) {
          errorString = `Response error: ${error.status} ${error.statusText} - ${error.url}`;
        } else if (typeof error === "object" && error !== null) {
          try {
            errorString = JSON.stringify(error);
          } catch {
            errorString = `[object ${error.constructor?.name || "Object"}]`;
          }
        } else {
          errorString = String(error);
        }

        scope.setExtra("error", errorString);

        // Check if this is an HTTP client error (like Axios error or apisauce response)
        if (
          typeof error === "object" &&
          error !== null &&
          "config" in error &&
          "data" in error
        ) {
          // Handle HTTP error - create a proper Error object with meaningful message
          // apisauce has `status` directly on response, Axios errors have it nested under `.response`
          const httpError = error as any;
          const status = httpError.status ?? httpError.response?.status;
          const message = `Request failed: ${httpError.config?.method?.toUpperCase()} ${
            httpError.config?.url
          } - ${status ?? "Unknown status"}`;
          Sentry.captureException(new Error(message));
        } else if (error instanceof Response) {
          // Handle Response objects specifically
          const message = `HTTP ${error.status}: ${error.statusText} - ${error.url}`;
          Sentry.captureException(new Error(message));
        } else {
          // Handle other errors - capture directly
          Sentry.captureException(error);
        }
      });
    }
  : (error: unknown, info: unknown = {}) => {
      // eslint-disable-next-line no-console
      console.error(
        "This error would be logged to sentry:",
        error,
        "Info would be logged to sentry:",
        info,
      );
    };
